import { useState } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '@/components/common/PageHeader.jsx';
import Field from '@/components/common/Field.jsx';
import StickyActions from '@/components/common/StickyActions.jsx';
import { LIST_TICKET_CATEGORIAS, LIST_SLA, CREAR_TICKET } from './gql.js';

const REFERENCIAS = [
  { value: 'REMITO', label: 'Remito' },
  { value: 'PARADA', label: 'Parada' },
  { value: 'OTRO', label: 'Otro' },
];

const PRIORIDADES = [
  { value: 'BAJA', label: 'Baja' },
  { value: 'MEDIA', label: 'Media' },
  { value: 'ALTA', label: 'Alta' },
  { value: 'CRITICA', label: 'Crítica' },
];

const CANALES = [
  { value: 'PORTAL', label: 'Portal' },
  { value: 'TELEFONO', label: 'Teléfono' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'INTERNO', label: 'Interno' },
];

const FORM_ID = 'ticket-create-form';

export default function TicketForm({ onCancel, onCreated }) {
  const [form, setForm] = useState({
    clienteId: '',
    referenciaTipo: 'OTRO',
    referenciaId: '',
    categoriaId: '',
    prioridad: 'MEDIA',
    canal: 'PORTAL',
    asunto: '',
    descripcion: '',
    slaId: '',
  });
  const [errors, setErrors] = useState({});

  const [{ data: categoriasData }] = useQuery({ query: LIST_TICKET_CATEGORIAS });
  const [{ data: slaData }] = useQuery({ query: LIST_SLA, variables: { pagination: { limit: 50, offset: 0 } } });
  const [, crearTicket] = useMutation(CREAR_TICKET);

  const categorias = categoriasData?.listTicketCategorias ?? [];
  const slaItems = slaData?.listSLA.items ?? [];

  const validate = () => {
    const nextErrors = {};
    if (!form.clienteId) {
      nextErrors.clienteId = 'Ingresá el cliente';
    }
    if (!form.asunto.trim()) {
      nextErrors.asunto = 'Ingresá un asunto';
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    const payload = {
      clienteId: Number(form.clienteId),
      referenciaTipo: form.referenciaTipo,
      referenciaId: form.referenciaId ? Number(form.referenciaId) : null,
      categoriaId: form.categoriaId || null,
      prioridad: form.prioridad,
      canal: form.canal,
      asunto: form.asunto,
      descripcion: form.descripcion || null,
      slaId: form.slaId || null,
    };
    const result = await crearTicket({ input: payload });
    if (result.error) {
      alert(result.error.message);
      return;
    }
    onCreated?.(result.data?.crearTicket?.id);
  };

  return (
    <div className="w-full">
      <PageHeader title="Nuevo ticket" subtitle="Alta manual de reclamo" />
      <form id={FORM_ID} onSubmit={handleSubmit} className="mx-auto mt-6 grid w-full max-w-[1400px] grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Datos principales</h2>
            <div className="mt-4 grid grid-cols-12 gap-4">
              <div className="col-span-12 sm:col-span-6">
                <Field label="Cliente" htmlFor="cliente" required error={errors.clienteId}>
                  <input
                    id="cliente"
                    type="number"
                    value={form.clienteId}
                    onChange={handleChange('clienteId')}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  />
                </Field>
              </div>
              <div className="col-span-12 sm:col-span-6">
                <Field label="Referencia" htmlFor="referenciaTipo">
                  <select
                    id="referenciaTipo"
                    value={form.referenciaTipo}
                    onChange={handleChange('referenciaTipo')}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    {REFERENCIAS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-12 sm:col-span-6">
                <Field label="ID de referencia" htmlFor="referenciaId">
                  <input
                    id="referenciaId"
                    type="number"
                    value={form.referenciaId}
                    onChange={handleChange('referenciaId')}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  />
                </Field>
              </div>
              <div className="col-span-12 sm:col-span-6">
                <Field label="Categoría" htmlFor="categoriaId">
                  <select
                    id="categoriaId"
                    value={form.categoriaId}
                    onChange={handleChange('categoriaId')}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    <option value="">Sin categoría</option>
                    {categorias.map(categoria => (
                      <option key={categoria.id} value={categoria.id}>
                        {categoria.nombre}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-12 sm:col-span-6">
                <Field label="Prioridad" htmlFor="prioridad">
                  <select
                    id="prioridad"
                    value={form.prioridad}
                    onChange={handleChange('prioridad')}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    {PRIORIDADES.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="col-span-12 sm:col-span-6">
                <Field label="Canal" htmlFor="canal">
                  <select
                    id="canal"
                    value={form.canal}
                    onChange={handleChange('canal')}
                    className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                  >
                    {CANALES.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
            </div>
          </section>

          <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">Contenido</h2>
            <div className="mt-4 space-y-4">
              <Field label="Asunto" htmlFor="asunto" required error={errors.asunto}>
                <input
                  id="asunto"
                  value={form.asunto}
                  onChange={handleChange('asunto')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
              <Field label="Descripción" htmlFor="descripcion" hint="Detalle claro del problema y contexto">
                <textarea
                  id="descripcion"
                  rows={6}
                  value={form.descripcion}
                  onChange={handleChange('descripcion')}
                  className="w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                />
              </Field>
            </div>
          </section>
        </div>

        <aside className="col-span-12 space-y-6 lg:col-span-4">
          <section className="rounded-2xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--panel),0.98)] p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-[rgb(var(--fg))]">SLA sugerido</h2>
            <Field label="Definición SLA" htmlFor="slaId" hint="Seleccioná una promesa si querés forzar la asignación">
              <select
                id="slaId"
                value={form.slaId}
                onChange={handleChange('slaId')}
                className="mt-2 w-full rounded-xl border border-[rgba(var(--border),0.6)] bg-[rgba(var(--subpanel),0.6)] px-3 py-2 text-sm text-[rgb(var(--fg))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
              >
                <option value="">Automático</option>
                {slaItems.map(item => (
                  <option key={item.id} value={item.id}>
                    {item.nombre} · {item.promesaHoras}h
                  </option>
                ))}
              </select>
            </Field>
          </section>
        </aside>
      </form>

      <StickyActions>
        <div className="flex w-full items-center justify-between gap-4">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-xl border border-[rgba(var(--border),0.6)] px-4 py-2 text-sm font-semibold text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--subpanel),0.6)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Cancelar
          </button>
          <button
            type="submit"
            form={FORM_ID}
            className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-semibold text-white shadow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Guardar ticket
          </button>
        </div>
      </StickyActions>
    </div>
  );
}
